import { useState } from 'react';
import { Plus, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import type { LearnedRuleRow } from '@command-room/shared';
import { useRules, useDeleteRule, useReembedRule } from '@/api/queries';
import { RuleDialog } from '@/pages/RulesPage';
import { TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { relativeTime } from '@/lib/format';

function LearningRow({ rule, onEdit }: { rule: LearnedRuleRow; onEdit: (r: LearnedRuleRow) => void }) {
  const deleteRule = useDeleteRule();
  const reembed = useReembedRule();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <tr className="hover:bg-muted/20">
      <td className="px-4 py-3 max-w-xl">
        <p className="line-clamp-3 text-sm">{rule.rule_text}</p>
      </td>
      <td className="px-4 py-3">
        <Badge variant={rule.source === 'sweeper' ? 'secondary' : 'outline'}>
          {rule.source}
        </Badge>
      </td>
      <td className="px-4 py-3">
        {rule.embedding_dirty ? (
          <Badge variant="warning">dirty</Badge>
        ) : (
          <Badge variant="success">ok</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime(rule.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {rule.embedding_dirty && (
            <Button
              variant="ghost"
              size="icon"
              title="Re-embed"
              disabled={reembed.isPending}
              onClick={() => reembed.mutate(rule.id)}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" title="Edit" onClick={() => onEdit(rule)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteRule.isPending}
                onClick={() =>
                  deleteRule.mutate(rule.id, { onSuccess: () => setConfirmDelete(false) })
                }
              >
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function AgentLearningsTab({ agentName }: { agentName: string }) {
  const { data: rules, isLoading } = useRules('agent', agentName);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LearnedRuleRow | undefined>(undefined);

  const dirtyCount = (rules ?? []).filter((r) => r.embedding_dirty).length;

  function openCreate() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(rule: LearnedRuleRow) {
    setEditing(rule);
    setDialogOpen(true);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {rules?.length ?? 0} learning{rules?.length === 1 ? '' : 's'}
          {dirtyCount > 0 && (
            <span className="ml-2 text-warning-foreground">
              · {dirtyCount} embedding{dirtyCount === 1 ? '' : 's'} need re-embed
            </span>
          )}
        </p>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New learning
        </Button>
      </div>

      {isLoading ? (
        <TableSkeleton widths={['w-64', 'w-20', 'w-16', 'w-24', 'w-16']} rows={4} />
      ) : !rules || rules.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No learnings recorded for this agent yet.
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Rule</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Embed</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rules.map((rule) => (
                <LearningRow key={rule.id} rule={rule} onEdit={openEdit} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RuleDialog
        key={editing?.id ?? 'new'}
        initial={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        lockedScope="agent"
        lockedTarget={agentName}
      />
    </div>
  );
}
